import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/* Pedido a sync-contacto-crm. Se manda el par de ids de UN tipo de contacto:
   - consorcista: consorcioId + consorcistaId (ya lo admite n8n)
   - prestador: administrationUserId + providerId (lo admite n8n desde 2026-10-16; lee my-providers/{providerId}) */
export type CrmSyncPayload =
    | { consorcioId: string; consorcistaId: string }
    | { administrationUserId: string; providerId: string };

/**
 * Actualiza el "contacto" del CRM del agente (el que llega en el historial de chats, ver MessageContact)
 * a partir del documento en Firestore. n8n lee la versión más reciente del documento, así que se llama
 * DESPUÉS de guardar el cambio. Si en el CRM el contacto tenía otro número de WhatsApp, n8n reasigna el perfil
 * al número nuevo.
 *
 * Endpoint: POST {agentePath}sync-contacto-crm (test: n8n.secretaria-247.com, prod: prod.secretaria-247.com).
 * Responde 400 si falta alguno de los dos ids.
 *
 * Los métodos sync* nunca tiran error: devuelven true/false y dejan el detalle en consola, para que una falla
 * de n8n no rompa el guardado en la plataforma.
 */
@Injectable({
    providedIn: 'root',
})
export class CrmSyncService {
    private http = inject(HttpClient);
    private endpoint = environment.agentePath + 'sync-contacto-crm';
    /* borrado del contacto (2026-10-29). Mismo body que sync-contacto-crm. Respuestas: 200 { action: 'deleted' }
       (borrado) o { action: 'unit_removed', remaining_units } (tenía otras unidades: solo se desvincula esa);
       404 { action: 'not_found' } (no estaba en el CRM); 400 si faltan los ids.
       Prestadores: por ahora n8n solo admite consorcioId + consorcistaId (con administrationUserId + providerId
       responde 400); se manda igual, con los mismos datos que el sync, para que funcione cuando lo amplíen */
    private deleteEndpoint = environment.agentePath + 'delete-contacto-crm';

    //después de crear o editar un consorcista
    syncConsorcista(consorcioId: string, consorcistaId: string): Promise<boolean> {
        return this.sync({ consorcioId, consorcistaId });
    }

    //después de crear o editar un prestador (users/{administrationUserId}/accounts/administration/my-providers/{providerId})
    syncProveedor(administrationUserId: string, providerId: string): Promise<boolean> {
        return this.sync({ administrationUserId, providerId });
    }

    //después de borrar un consorcista en Firestore: lo saca del CRM del agente (o solo esa unidad, si tenía otras)
    deleteConsorcista(consorcioId: string, consorcistaId: string): Promise<boolean> {
        return this.delete({ consorcioId, consorcistaId });
    }

    //después de borrar un prestador en Firestore (n8n todavía no lo admite: ver deleteEndpoint)
    deleteProveedor(administrationUserId: string, providerId: string): Promise<boolean> {
        return this.delete({ administrationUserId, providerId });
    }

    //como sync: nunca tira error. 404 (no estaba en el CRM) cuenta como hecho: no hay nada que borrar
    private async delete(payload: CrmSyncPayload): Promise<boolean> {
        const faltante = Object.entries(payload).find(([, valor]) => !valor);
        if (faltante) {
            console.warn('[delete-contacto-crm] No se envía: falta ' + faltante[0], payload);
            return false;
        }
        try {
            await firstValueFrom(this.http.post<any>(this.deleteEndpoint, payload, {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            }));
            return true;
        } catch (error: any) {
            if (error?.status === 404) {
                return true;
            }
            console.warn('[delete-contacto-crm] No se pudo borrar el contacto', payload, error);
            return false;
        }
    }

    private async sync(payload: CrmSyncPayload): Promise<boolean> {
        const faltante = Object.entries(payload).find(([, valor]) => !valor);
        if (faltante) {
            console.warn('[sync-contacto-crm] No se envía: falta ' + faltante[0], payload);
            return false;
        }
        try {
            await this.post(payload);
            return true;
        } catch (error) {
            console.warn('[sync-contacto-crm] No se pudo sincronizar el contacto', payload, error);
            return false;
        }
    }

    //pedido directo, por si hace falta el error o la respuesta (los métodos sync* son los de uso normal)
    post(payload: CrmSyncPayload): Promise<any> {
        return firstValueFrom(this.http.post<any>(this.endpoint, payload, {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        }));
    }
}
